import React, { useState, useEffect } from "react";
import format from "date-fns/format";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import {
  createBookingPaymentIntent,
  getBookingQuote,
} from "../../../components/api/api";
import Button from "../../../components/Button/Button";

import "./BookingModal.scss";

function buildGiftEconomyQuoteFallback(cleaningFee, nights) {
  if (!cleaningFee || cleaningFee <= 0) return null;
  const subtotal = Math.round(cleaningFee * 100) / 100;
  const hst = Math.round(cleaningFee * 0.14 * 100) / 100;
  const preCreditTotal = Math.round((subtotal + hst) * 100) / 100;
  const creditCardFee = Math.round(preCreditTotal * 0.03 * 100) / 100;
  return {
    nights,
    pricingModel: "gift_economy",
    cleaningFee,
    nightlyRate: 0,
    weekdayNightlyRate: null,
    weekdayNights: 0,
    weekendNights: 0,
    weekdaySubtotal: 0,
    weekendSubtotal: 0,
    hasWeekdayRateOverride: false,
    nightlySubtotal: 0,
    subtotal,
    hst,
    creditCardFee,
    etransferTotal: preCreditTotal,
    creditCardTotal: Math.round((preCreditTotal + creditCardFee) * 100) / 100,
    requiresPayment: preCreditTotal > 0,
  };
}

const StripePaymentForm = ({
  onPaymentSuccess,
  onPaymentError,
  isSubmitting,
  onSubmit,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentError, setPaymentError] = useState("");

  useEffect(() => {
    if (onSubmit && stripe && elements && isSubmitting) {
      const handlePayment = async () => {
        try {
          const result = await stripe.confirmPayment({
            elements,

            redirect: "if_required",
          });

          if (result.error) {
            console.error("Stripe payment error:", result.error);

            setPaymentError(result.error.message);

            onPaymentError(result.error.message);
          } else if (
            result.paymentIntent &&
            result.paymentIntent.status === "succeeded"
          ) {
            setPaymentError("");

            onPaymentSuccess();
          }
        } catch (error) {
          console.error("Stripe payment exception:", error);
          setPaymentError("Payment failed. Please try again.");
          onPaymentError("Payment failed. Please try again.");
        }
      };

      handlePayment();
    }
  }, [
    onSubmit,
    stripe,
    elements,
    isSubmitting,
    onPaymentSuccess,
    onPaymentError,
  ]);

  return (
    <div
      className={`stripe-payment-container ${
        paymentError ? "payment-error" : ""
      }`}
    >
      <PaymentElement />

      {paymentError && (
        <div className="payment-error-message">
          <span className="error-icon">⚠️</span>

          <span>{paymentError}</span>
        </div>
      )}
    </div>
  );
};

const BookingModal = ({
  isOpen,
  onClose,
  startDate,
  endDate,
  propertyType,
  propertyId,
  propertyConfig,
  onConfirm,
}) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [stripePublishableKey, setStripePublishableKey] = useState("");
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState("");
  const [payWithCreditCard, setPayWithCreditCard] = useState(false);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(null);
  const displayName = propertyConfig?.displayName || propertyType;
  const pricingModel =
    quote?.pricingModel || propertyConfig?.pricingModel || "gift_economy";
  const isFixedNightly = pricingModel === "fixed_nightly";
  const nights =
    quote?.nights ?? Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
  const effectiveQuote =
    quote ??
    (!isFixedNightly
      ? buildGiftEconomyQuoteFallback(propertyConfig?.cleaningFee ?? 0, nights)
      : null);
  const requiresPayment = effectiveQuote?.requiresPayment ?? false;
  const etransferTotal = effectiveQuote?.etransferTotal ?? 0;
  const creditCardTotal = effectiveQuote?.creditCardTotal ?? 0;
  const displayTotal = payWithCreditCard ? creditCardTotal : etransferTotal;

  const paymentHeader =
    propertyConfig?.paymentHeader ||
    (isFixedNightly
      ? "Your stay total is due at booking."
      : "We're offering this space on a gift-economy basis.");

  const paymentBody =
    propertyConfig?.paymentBody ||
    (isFixedNightly
      ? "You'll pay the full amount (nightly rate, cleaning fee, and taxes) to confirm your reservation."
      : "After your stay you can give back in whatever way feels fair, reasonable, or inspiring.");

  useEffect(() => {
    if (!isOpen) {
      setIsSubmitting(false);
      setError("");
      setBookingComplete(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/config`)
        .then((res) => res.json())
        .then((config) => {
          setStripePublishableKey(config.publishableKey);
        })
        .catch((err) => {
          console.error("Error fetching publishable key:", err);
        });
    }
  }, [isOpen]);

  useEffect(() => {
    if (stripePublishableKey) {
      setStripePromise(loadStripe(stripePublishableKey));
    }
  }, [stripePublishableKey]);

  useEffect(() => {
    if (!isOpen || !propertyId || !startDate || !endDate) {
      setQuote(null);
      return;
    }

    const startDateStr = format(startDate, "yyyy-MM-dd");
    const endDateStr = format(endDate, "yyyy-MM-dd");

    setQuoteLoading(true);

    getBookingQuote(propertyId, startDateStr, endDateStr)
      .then((data) => {
        setQuote(data);
      })

      .finally(() => {
        setQuoteLoading(false);
      });
  }, [isOpen, propertyId, startDate, endDate]);

  useEffect(() => {
    if (!payWithCreditCard || !isOpen || !requiresPayment || !propertyId) {
      setClientSecret("");

      return;
    }

    const startDateStr = format(startDate, "yyyy-MM-dd");

    const endDateStr = format(endDate, "yyyy-MM-dd");

    createBookingPaymentIntent(propertyId, startDateStr, endDateStr).then(
      (secret) => {
        setClientSecret(secret);
      },
    );
  }, [
    payWithCreditCard,
    isOpen,
    requiresPayment,
    propertyId,
    startDate,
    endDate,
  ]);

  if (!isOpen) return null;

  const buildConfirmPayload = (paymentMethod, totalAmount) => ({
    rentalName: propertyType,
    name: name.trim(),
    email: email.trim(),
    rentalStart: startDate,
    rentalEnd: endDate,
    paymentMethod,
    totalAmount,
    pricingModel,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim() || !email.trim()) {
      setError("Please fill in all fields");

      return;
    }

    if (quoteLoading) {
      setError("Loading pricing, please wait...");

      return;
    }

    setIsSubmitting(true);

    setError("");

    try {
      if (!requiresPayment) {
        await onConfirm(buildConfirmPayload("none", 0));

        showBookingComplete("none");
      } else if (!payWithCreditCard) {
        await onConfirm(buildConfirmPayload("etransfer", etransferTotal));

        showBookingComplete("etransfer");
      }
    } catch (err) {
      console.error("Booking error:", err);

      setError("Booking failed. Please try again.");

      setIsSubmitting(false);
    }
  };

  const handleStripePaymentSuccess = async () => {
    try {
      await onConfirm(buildConfirmPayload("credit", creditCardTotal));

      showBookingComplete("credit");
    } catch (err) {
      setError("Booking failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStripePaymentError = (errorMessage) => {
    setError(errorMessage);

    setIsSubmitting(false);
  };

  const handleClose = () => {
    if (bookingComplete) {
      handleDismiss();
      return;
    }

    if (!isSubmitting) {
      setName("");
      setEmail("");
      setError("");
      setPayWithCreditCard(false);
      setClientSecret("");
      setQuote(null);
      onClose();
    }
  };

  const showBookingComplete = (paymentMethod) => {
    setIsSubmitting(false);
    setBookingComplete({
      paymentMethod,
      email: email.trim(),
      etransferTotal,
      creditCardTotal,
      startDate,
      endDate,
      nights,
    });
  };

  const handleDismiss = () => {
    setIsSubmitting(false);
    setBookingComplete(null);
    setName("");
    setEmail("");
    setError("");
    setPayWithCreditCard(false);
    setClientSecret("");
    setQuote(null);
    onClose();
  };

  const togglePaymentMethod = () => {
    setPayWithCreditCard(!payWithCreditCard);

    setError("");
  };

  return (
    <div className="booking-modal-overlay" onClick={handleClose}>
      <div className="booking-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{bookingComplete ? "Booking Confirmed" : "Book Your Stay"}</h2>

          <button
            className="close-button"
            onClick={handleClose}
            disabled={isSubmitting && !bookingComplete}
          >
            ×
          </button>
        </div>

        <div className="modal-content">
          {bookingComplete ? (
            <div className="booking-complete">
              <div className="property-info">
                <h3>{displayName}</h3>
                <div className="selected-dates">
                  <p className="arrival-date">
                    <strong>Check In:</strong>
                    <p>
                      After 2pm,{" "}
                      {format(bookingComplete.startDate, "EEEE, MMMM d")}
                    </p>
                  </p>
                  <p className="departure-date">
                    <strong>Check Out:</strong>
                    <p>
                      Before 11am,{" "}
                      {format(bookingComplete.endDate, "EEEE, MMMM d")}
                    </p>
                  </p>
                  <p className="nights-count">
                    <strong>Nights:</strong> {bookingComplete.nights}
                  </p>
                </div>
              </div>

              <div className="payment-notice">
                <div className="notice-icon">✓</div>
                <div className="notice-content">
                  <h4>Booking confirmed</h4>
                  <p>
                    A confirmation email was sent to{" "}
                    <strong>{bookingComplete.email}</strong>.
                  </p>

                  {bookingComplete.paymentMethod === "etransfer" && (
                    <>
                      <strong>
                        Send ${bookingComplete.etransferTotal.toFixed(2)} by
                        e-transfer to maplegrovepermaculture@gmail.com
                      </strong>
                      <p className="important">
                        <strong>Important:</strong> Payment must be received
                        within 24 hours or the booking will be cancelled.
                      </p>
                    </>
                  )}

                  {bookingComplete.paymentMethod === "credit" && (
                    <p>
                      Your payment of{" "}
                      <strong>
                        ${bookingComplete.creditCardTotal.toFixed(2)}
                      </strong>{" "}
                      has been processed.
                    </p>
                  )}
                </div>
              </div>

              <div className="booking-complete-actions">
                <Button type="button" onClick={handleDismiss}>
                  Done
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="property-info">
                <h3>{displayName}</h3>

                <div className="selected-dates">
                  <p className="arrival-date">
                    <strong>Check In:</strong>

                    <p>After 2pm, {format(startDate, "EEEE, MMMM d")}</p>
                  </p>

                  <p className="departure-date">
                    <strong>Check Out:</strong>

                    <p>Before 11am, {format(endDate, "EEEE, MMMM d")}</p>
                  </p>

                  <p className="nights-count">
                    <strong>Nights:</strong> {nights}
                  </p>
                </div>
              </div>

              <div className="payment-notice">
                <div className="notice-icon">💳</div>

                <div className="notice-content">
                  <h4>Payment Information</h4>

                  <strong>{paymentHeader}</strong>

                  <p>{paymentBody}</p>

                </div>
              </div>

              <form onSubmit={handleSubmit} className="booking-form">
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>

                  <input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter your full name"
                    required
                    disabled={isSubmitting}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>

                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    required
                    disabled={isSubmitting}
                  />
                </div>

                {quoteLoading && (
                  <p className="quote-loading">Calculating your total...</p>
                )}

                {!quoteLoading && requiresPayment && effectiveQuote && (
                  <>
                    <div className="payment-summary">
                      <h4>Payment Summary</h4>

                      <div className="payment-breakdown">
                        {isFixedNightly &&
                          effectiveQuote.nightlySubtotal > 0 && (
                            <>
                              {effectiveQuote.hasWeekdayRateOverride &&
                                effectiveQuote.weekdayNights > 0 &&
                                effectiveQuote.weekdayNightlyRate != null && (
                                  <div className="payment-line">
                                    <span>
                                      $
                                      {effectiveQuote.weekdayNightlyRate.toFixed(
                                        2,
                                      )}{" "}
                                      × {effectiveQuote.weekdayNights} weekday
                                      night
                                      {effectiveQuote.weekdayNights !== 1
                                        ? "s"
                                        : ""}
                                      :
                                    </span>

                                    <span>
                                      $
                                      {effectiveQuote.weekdaySubtotal.toFixed(
                                        2,
                                      )}
                                    </span>
                                  </div>
                                )}

                              {(!effectiveQuote.hasWeekdayRateOverride ||
                                effectiveQuote.weekendNights > 0) &&
                                effectiveQuote.nightlyRate > 0 && (
                                  <div className="payment-line">
                                    <span>
                                      ${effectiveQuote.nightlyRate.toFixed(2)} ×{" "}
                                      {effectiveQuote.hasWeekdayRateOverride
                                        ? effectiveQuote.weekendNights
                                        : effectiveQuote.nights}{" "}
                                      {effectiveQuote.hasWeekdayRateOverride
                                        ? "weekend "
                                        : ""}
                                      night
                                      {(effectiveQuote.hasWeekdayRateOverride
                                        ? effectiveQuote.weekendNights
                                        : effectiveQuote.nights) !== 1
                                        ? "s"
                                        : ""}
                                      :
                                    </span>

                                    <span>
                                      $
                                      {(effectiveQuote.hasWeekdayRateOverride
                                        ? effectiveQuote.weekendSubtotal
                                        : effectiveQuote.nightlySubtotal
                                      ).toFixed(2)}
                                    </span>
                                  </div>
                                )}
                            </>
                          )}

                        {effectiveQuote.cleaningFee > 0 && (
                          <div className="payment-line">
                            <span>Cleaning Fee:</span>

                            <span>
                              ${effectiveQuote.cleaningFee.toFixed(2)}
                            </span>
                          </div>
                        )}

                        <div className="payment-line">
                          <span>HST (14%):</span>

                          <span>${effectiveQuote.hst.toFixed(2)}</span>
                        </div>

                        {payWithCreditCard && (
                          <div className="payment-line">
                            <span>Credit Card Fee (3%):</span>

                            <span>
                              ${effectiveQuote.creditCardFee.toFixed(2)}
                            </span>
                          </div>
                        )}

                        <div className="payment-line total">
                          <span>Total:</span>

                          <span>${displayTotal.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="payment-method-section">
                      <h4>Payment Method</h4>

                      <button
                        type="button"
                        className="payment-toggle-button"
                        onClick={togglePaymentMethod}
                        disabled={isSubmitting}
                      >
                        {payWithCreditCard
                          ? "Pay with E-Transfer"
                          : "Pay with Credit Card"}
                      </button>

                      {payWithCreditCard && stripePromise && clientSecret && (
                        <Elements
                          stripe={stripePromise}
                          options={{ clientSecret }}
                        >
                          <StripePaymentForm
                            onPaymentSuccess={handleStripePaymentSuccess}
                            onPaymentError={handleStripePaymentError}
                            isSubmitting={isSubmitting}
                            onSubmit={isSubmitting}
                          />
                        </Elements>
                      )}

                      {!payWithCreditCard && (
                        <div className="etransfer-instructions">
                          <p>
                            <strong>E-Transfer Instructions:</strong>
                          </p>

                          <p>
                            Send an E-Transfer of{" "}
                            <strong>${etransferTotal.toFixed(2)}</strong> to:
                          </p>

                          <p className="etransfer-email">
                            maplegrovepermaculture@gmail.com
                          </p>

                          <p>
                            If payment is not recieved within 24 hours, the
                            booking will be cancelled.
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                )}

                <div className="form-actions">
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="confirm-button"
                    disabled={
                      isSubmitting ||
                      quoteLoading ||
                      (payWithCreditCard && requiresPayment && !clientSecret)
                    }
                  >
                    {isSubmitting
                      ? payWithCreditCard
                        ? "Processing Payment..."
                        : "Processing..."
                      : "Confirm Booking"}
                  </button>
                </div>

                {isSubmitting && payWithCreditCard && requiresPayment && (
                  <div className="loading-feedback">
                    <div className="loading-spinner"></div>

                    <p>Processing your payment securely...</p>
                  </div>
                )}
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingModal;
